// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Rediger › Geometri's guides and dimension chains as kept (frontend-u0o.11):
 * the list, its reducer, and its storage. The building model has no
 * annotation records yet (ReUseX reusex-8cug) and ruxd no per-case
 * key-value store, so they live in this browser's `localStorage`, per user
 * and case (`settingsStorage.annotationsKey`) — the UI says "lokalt". Pure:
 * no React, no DOM.
 */

import type { DimChain, Guide, NewDimChain, NewGuide } from './cadMeasure';
import type { XY } from './planWalls';

export interface Annotations {
  guides: Guide[];
  dims: DimChain[];
  /** The next id's number ("a7"). */
  seq: number;
}

export const EMPTY_ANNOTATIONS: Annotations = { guides: [], dims: [], seq: 1 };

export type AnnotationKind = 'guides' | 'dims';

export type AnnotationAction =
  | { type: 'add-guide'; guide: NewGuide }
  | { type: 'add-dim'; dim: NewDimChain }
  | { type: 'remove'; id: string }
  /** Delete in Vælg: every selected guide and chain. */
  | { type: 'remove-many'; ids: readonly string[] }
  /** A grip edit in Vælg: the guide or chain with this id, as it is now. */
  | { type: 'update'; item: Guide | DimChain }
  /** ⌫ in Mål / Målsætning: the newest of that kind on the shown storey. */
  | { type: 'remove-last'; what: AnnotationKind; storeyId: number | null }
  /** Ryd: a kind (or both), on one storey or (no `storeyId`) everywhere. */
  | { type: 'clear'; what: AnnotationKind | 'all'; storeyId?: number | null }
  | { type: 'load'; state: Annotations };

const onShown = (storeyId: number | null) => (x: { storeyId: number | null }) => storeyId === null || x.storeyId === storeyId;

export function annotationsReducer(state: Annotations, action: AnnotationAction): Annotations {
  switch (action.type) {
    case 'add-guide':
      return { ...state, guides: [...state.guides, { ...action.guide, id: `a${state.seq}` } as Guide], seq: state.seq + 1 };
    case 'add-dim':
      return { ...state, dims: [...state.dims, { ...action.dim, id: `a${state.seq}` }], seq: state.seq + 1 };
    case 'remove': {
      const guides = state.guides.filter((g) => g.id !== action.id);
      const dims = state.dims.filter((d) => d.id !== action.id);
      return guides.length === state.guides.length && dims.length === state.dims.length ? state : { ...state, guides, dims };
    }
    case 'remove-many': {
      const ids = new Set(action.ids);
      const guides = state.guides.filter((g) => !ids.has(g.id));
      const dims = state.dims.filter((d) => !ids.has(d.id));
      return guides.length === state.guides.length && dims.length === state.dims.length ? state : { ...state, guides, dims };
    }
    case 'update': {
      const item = action.item;
      if ('kind' in item) {
        if (!state.guides.some((g) => g.id === item.id)) return state;
        return { ...state, guides: state.guides.map((g) => (g.id === item.id ? item : g)) };
      }
      if (!state.dims.some((d) => d.id === item.id)) return state;
      return { ...state, dims: state.dims.map((d) => (d.id === item.id ? item : d)) };
    }
    case 'remove-last': {
      const list: readonly { id: string; storeyId: number | null }[] = action.what === 'guides' ? state.guides : state.dims;
      const last = [...list].reverse().find(onShown(action.storeyId));
      return last ? annotationsReducer(state, { type: 'remove', id: last.id }) : state;
    }
    case 'clear': {
      const keep = (x: { storeyId: number | null }) => action.storeyId !== undefined && !onShown(action.storeyId)(x);
      return {
        ...state,
        guides: action.what === 'dims' ? state.guides : state.guides.filter(keep),
        dims: action.what === 'guides' ? state.dims : state.dims.filter(keep),
      };
    }
    case 'load':
      return action.state;
  }
}

// ---------------------------------------------------------- undo / redo --

/**
 * The list with its own undo and redo (Vælg's edits, Mål's and
 * Målsætning's additions, Ryd): every change is a step; ⌘Z takes the
 * newest step of the model or of this list, whichever came last
 * (`editJournal.ts`).
 */
export interface AnnotationHistory {
  present: Annotations;
  /** Oldest first. */
  past: Annotations[];
  /** The next redo first. */
  future: Annotations[];
}

/** How many steps back the list keeps. */
export const HISTORY_LIMIT = 100;

export type AnnotationHistoryAction = AnnotationAction | { type: 'undo' } | { type: 'redo' };

export const initialHistory = (present: Annotations): AnnotationHistory => ({ present, past: [], future: [] });

export function historyReducer(h: AnnotationHistory, action: AnnotationHistoryAction): AnnotationHistory {
  if (action.type === 'undo') {
    if (h.past.length === 0) return h;
    return { present: h.past[h.past.length - 1], past: h.past.slice(0, -1), future: [h.present, ...h.future] };
  }
  if (action.type === 'redo') {
    if (h.future.length === 0) return h;
    return { present: h.future[0], past: [...h.past, h.present], future: h.future.slice(1) };
  }
  // Another case's list (or the first read): no steps to go back to.
  if (action.type === 'load') return initialHistory(action.state);
  const next = annotationsReducer(h.present, action);
  if (next === h.present) return h;
  return { present: next, past: [...h.past, h.present].slice(-HISTORY_LIMIT), future: [] };
}

export const canUndoAnnotations = (h: AnnotationHistory) => h.past.length > 0;
export const canRedoAnnotations = (h: AnnotationHistory) => h.future.length > 0;

/** How many of each the shown storey has (null: all). */
export function annotationCounts(state: Annotations, storeyId: number | null): { guides: number; dims: number } {
  const shown = onShown(storeyId);
  return { guides: state.guides.filter(shown).length, dims: state.dims.filter(shown).length };
}

// ---------------------------------------------------------------- storage --

export interface AnnotationStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem?(key: string): void;
}

/** The stored blob's version; bump it with a reader for the old one. */
const VERSION = 1;

const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isXY = (v: unknown): v is XY => Array.isArray(v) && v.length === 2 && num(v[0]) && num(v[1]);
const storeyOf = (v: unknown): number | null | undefined => (v === null ? null : num(v) ? v : undefined);
const isUnit = (v: unknown): v is XY => isXY(v) && Math.abs(Math.hypot(v[0], v[1]) - 1) < 1e-3;

function readGuide(v: unknown): Guide | null {
  if (!v || typeof v !== 'object') return null;
  const g = v as Record<string, unknown>;
  const storeyId = storeyOf(g.storeyId);
  if (typeof g.id !== 'string' || storeyId === undefined) return null;
  if (g.kind === 'line' && isXY(g.a) && isUnit(g.dir)) return { id: g.id, kind: 'line', storeyId, a: [g.a[0], g.a[1]], dir: [g.dir[0], g.dir[1]] };
  if (g.kind === 'point' && isXY(g.p)) return { id: g.id, kind: 'point', storeyId, p: [g.p[0], g.p[1]] };
  return null;
}

function readDim(v: unknown): DimChain | null {
  if (!v || typeof v !== 'object') return null;
  const d = v as Record<string, unknown>;
  const storeyId = storeyOf(d.storeyId);
  if (typeof d.id !== 'string' || storeyId === undefined || !isUnit(d.dir) || !num(d.offset) || !Array.isArray(d.points)) return null;
  if (d.points.length < 2 || !d.points.every(isXY)) return null;
  return { id: d.id, storeyId, points: (d.points as XY[]).map((p) => [p[0], p[1]] as XY), dir: [d.dir[0], d.dir[1]], offset: d.offset };
}

/** The annotations in a stored string; anything garbled is left out, an unknown version reads as none. */
export function parseAnnotations(raw: string | null): Annotations {
  if (!raw) return EMPTY_ANNOTATIONS;
  let blob: unknown;
  try {
    blob = JSON.parse(raw);
  } catch {
    return EMPTY_ANNOTATIONS;
  }
  if (!blob || typeof blob !== 'object' || (blob as { v?: unknown }).v !== VERSION) return EMPTY_ANNOTATIONS;
  const b = blob as { guides?: unknown; dims?: unknown };
  const guides = (Array.isArray(b.guides) ? b.guides : []).map(readGuide).filter((g): g is Guide => g !== null);
  const dims = (Array.isArray(b.dims) ? b.dims : []).map(readDim).filter((d): d is DimChain => d !== null);
  const seq = [...guides, ...dims].reduce((max, x) => Math.max(max, Number(/^a(\d+)$/.exec(x.id)?.[1] ?? 0)), 0) + 1;
  return { guides, dims, seq };
}

export function restoreAnnotations(key: string, storage: AnnotationStorage | null | undefined): Annotations {
  try {
    return parseAnnotations(storage?.getItem(key) ?? null);
  } catch {
    return EMPTY_ANNOTATIONS;
  }
}

export function storeAnnotations(key: string, state: Annotations, storage: AnnotationStorage | null | undefined): void {
  try {
    if (state.guides.length === 0 && state.dims.length === 0) storage?.removeItem?.(key);
    else storage?.setItem(key, JSON.stringify({ v: VERSION, guides: state.guides, dims: state.dims }));
  } catch {
    // A refused write only means they are not kept past a reload.
  }
}
